import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { KnxDevicesSnapshot, KnxWeatherSnapshot } from '../version.service';
import { DeepLinkService } from '../shared/deep-link.service';

/** KNX sub-tabs. To add one: extend this union, append to `subTabs` and add a matching
 *  *ngSwitchCase block to the template. (The room temperatures live in the Klima tab: KnxRoomsComponent.) */
export type KnxSubTab = 'lights' | 'sockets' | 'blinds' | 'weather';

/** Sub-tab shown without a link or a stored choice. */
const DEFAULT_SUB_TAB: KnxSubTab = 'lights';

@Component({
  selector: 'app-knx',
  templateUrl: './knx.component.html',
  styles: [`
    .knx-subtabs    { display: flex; background: var(--bs-tertiary-bg); border-radius: 10px; padding: 3px; gap: 3px; max-width: 100%;
                      overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: none; }
    /* Below md (the app's mobile view, < 768 px — also landscape phones): segments sharing the width by their
       label's length (all four fit at 360 px with 11 px labels, "Steckdosen" included), icon above the label (no
       sideways scrolling); full labels in one row only from 768 px */
    .knx-subtabs > .nav-tab-btn { flex: 1 1 auto; min-width: 0; min-height: 44px; line-height: 1.15; }
    .knx-subtab-icon  { display: block; font-size: 1rem; }
    .knx-subtab-label { display: block; font-size: .6875rem; overflow: hidden; text-overflow: ellipsis; }
    @media (min-width: 768px) {
      .knx-subtabs { display: inline-flex; }
      .knx-subtabs > .nav-tab-btn { flex: 0 0 auto; min-height: 0; line-height: 1.5; }
      .knx-subtab-icon, .knx-subtab-label { display: inline; font-size: inherit; }
      .knx-subtab-icon { margin-right: .3em; }
    }
  `],
})
export class KnxComponent implements OnInit, OnDestroy {
  /** Lights / sockets / blinds snapshot (null until the first frame) and its client receive time. */
  @Input() knxDevices:          KnxDevicesSnapshot | null               = null;
  @Input() knxDevicesUpdatedAt: number | null                           = null;
  @Input() knxDevicesFlash     = false;
  /** KNX weather station snapshot (null until the first "knxWeather" frame). */
  @Input() knxWeather:          KnxWeatherSnapshot | null               = null;
  @Input() liveDotOn           = true;
  @Input() nowTick             = Date.now();
  @Input() darkMode            = false;

  // ── Sub-tabs (data-driven; in the deep link as "#knx/<sub-tab>"; a bare "#knx" shows the one persisted per browser) ──
  private static readonly SUB_TAB_KEY = 'knxSubTab';
  /** `short`: an optional shorter label for phones, where the segments share the width (full label as tooltip). */
  readonly subTabs: { key: KnxSubTab; label: string; short?: string; icon: string }[] = [
    { key: 'lights',  label: 'KNX_TAB_LIGHTS',  icon: '💡' },
    { key: 'sockets', label: 'KNX_TAB_SOCKETS', icon: '🔌' },
    { key: 'blinds',  label: 'KNX_TAB_BLINDS',  icon: '🪟' },
    { key: 'weather', label: 'KNX_TAB_WEATHER', icon: '🌦' },
  ];
  subTab: KnxSubTab;

  private linkSub?: Subscription;

  constructor(private readonly link: DeepLinkService) {
    const linked = this.linkSubTab();
    this.subTab = linked ?? this.loadSubTab();
    if (linked) this.storeSubTab(linked);
  }

  ngOnInit(): void {
    // The link's sub-tab, again on back / forward / an edited link
    this.applyLink();
    this.linkSub = this.link.external$.subscribe(() => this.applyLink());
  }

  ngOnDestroy(): void {
    this.linkSub?.unsubscribe();
  }

  /** Show a sub-tab; a user's choice (not `fromLink`) goes into the link: "#knx/<key>", since a bare "#knx"
   *  opens the recipient's persisted sub-tab. */
  setSubTab(key: KnxSubTab, fromLink = false): void {
    this.subTab = key;
    this.storeSubTab(key);
    if (!fromLink) this.link.openView(`knx/${key}`);
  }

  trackSubTab = (_: number, s: { key: KnxSubTab }) => s.key;

  private storeSubTab(key: KnxSubTab): void {
    try { localStorage.setItem(KnxComponent.SUB_TAB_KEY, key); } catch { /* storage unavailable */ }
  }

  /** The persisted sub-tab; an unknown one (e.g. the old "temperatures", now in Klima) falls back to Lichter. */
  private loadSubTab(): KnxSubTab {
    try {
      const saved = localStorage.getItem(KnxComponent.SUB_TAB_KEY);
      if (saved && this.subTabs.some(t => t.key === saved)) return saved as KnxSubTab;
    } catch { /* storage unavailable (private mode, blocked site data) */ }
    return DEFAULT_SUB_TAB;
  }

  /**
   * The sub-tab the link names: "#knx/lights", "#knx/weather/<series>" … Null for a link without a KNX
   * sub-path ("#knx", or another tab's link on desktop): the shown / persisted one stays. `applyLink` then
   * names it in a bare "#knx" (the mobile tab button, an old link) — but never in another tab's link (on
   * desktop that would take the link away from the tab it names).
   */
  private linkSubTab(): KnxSubTab | null {
    const child = this.link.childOf('knx');
    return this.subTabs.find(t => t.key === child)?.key ?? null;
  }

  /** Applies the link (on init and when the browser navigates): the sub-tab. */
  private applyLink(): void {
    const link = this.link;
    if (link.tab !== 'knx') return;
    const sub = this.linkSubTab();
    if (sub && sub !== this.subTab) this.setSubTab(sub, true);
    // A bare "#knx" (the mobile tab button, an old link) shows the persisted sub-tab: name it, so the URL
    // (and a copied link) opens the same one. Never in another tab's link (desktop: it keeps its tab).
    if (link.childOf('knx') == null) link.openView(`knx/${this.subTab}`);
  }
}
